import { campaignsApi } from "@/api/campaign.api";
import {
  Campaign,
  ConfigurationRequest,
  ConfigurationSuggestion,
  CountsResponse,
  SuggestionsRequest,
} from "@/types/campaign";
import { api, queryClient } from "@/utils/api";
import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from "@tanstack/react-query";

export function getConfigurationCounts(
  configurationRequest: ConfigurationRequest,
) {
  return queryOptions<CountsResponse[]>({
    // Revalidate counts when filters change
    queryKey: ["counts", configurationRequest] as const,
    enabled: Array.isArray(configurationRequest)
      ? configurationRequest.length > 0
      : true,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    queryFn: () => {
      return api
        .post("apps/sales/configuration/counts", {
          json: configurationRequest,
        })
        .json<CountsResponse[]>();
    },
  });
}

/** Counts for a saved campaign, excluding leads it has already delivered. */
export const getCampaignConfigurationCounts = (
  campaign: Pick<Campaign, "id" | "configurations" | "stopLists">,
) =>
  getConfigurationCounts({
    configurations: campaign.configurations,
    stopListIds: campaign.stopLists?.map((sL) => sL.uniqueId.toString()) ?? [],
    campaignId: campaign.id,
  });

export const useConfigurationCounts = (req: ConfigurationRequest) =>
  useQuery(getConfigurationCounts(req), queryClient);

/**
 * const suggestions = await campaignsApi.fetchSuggestions({
        baseFilters,
        contactConfiguration,
        leadsPerWeek:
          campaign.type === "Scheduled"
            ? campaign.schedules.reduce(
                (sum, schedule) => sum + schedule.amount,
                0,
              )
            : (campaign.maxLeadsPerWeek ?? 0),
      });
 */

export function getConfigurationSuggestions({
  configuration,
  campaignId,
  count,
  leadsPerWeek,
  stopListIds,
}: Partial<SuggestionsRequest>) {
  return queryOptions({
    queryKey: [
      "suggested-configurations",
      configuration,
      count,
      leadsPerWeek,
      stopListIds,
    ],
    enabled: !!configuration,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 60 * 5 * 5,
    queryFn: async (): Promise<ConfigurationSuggestion[]> => {
      if (!configuration) return new Promise((resolve) => resolve([]));
      const suggestions = await campaignsApi.fetchSuggestions({
        configuration,
        count: count ?? 3,
        campaignId,
        stopListIds: stopListIds ?? [],
        leadsPerWeek,
        doNotModify: [],
      });
      return suggestions.map((s) => ({
        ...s,
        configuration: {
          ...s.configuration,
          enabled: s.configuration.quality === 1,
        },
      }));
    },
  });
}
